import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Input,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { FiDownload, FiRefreshCcw, FiSearch, FiTrash2 } from "react-icons/fi";
import "react-datepicker/dist/react-datepicker.css";
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination";
import AddFile from "./AddFile";
import type { FolderPublic, FileDetailWithStatus } from "@/client/types.gen";

// UI용 타입
export interface FileItem {
  name: string;
  size: number;
  lastModified: string;
  url: string;
  document_id?: string;
  status?: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  error_message?: string;
  object_name: string;
}

// API 응답을 UI 타입으로 변환하는 함수
export const convertToFileItem = (file: FileDetailWithStatus): FileItem => ({
  name: file.name,
  size: file.size,
  lastModified: file.last_modified || new Date().toISOString(),
  url: "", // Presigned URL은 필요시 별도 요청
  document_id: file.document_id || undefined,
  status: file.status || undefined,
  error_message: file.error_message || undefined,
  object_name: file.object_name,
});

export interface FileListProps {
  selectedFolderId?: string;
  fileState: {
    files: FileItem[];
    selectedFiles: string[];
    hoveredFileId?: string;
    onFileSelect: (fileId: string) => void;
    onFileHover: (fileId?: string) => void;
    onDeleteFiles: () => void;
    onDownload: (fileName: string) => void;
  };
  searchState: {
    searchQuery: string;
    onSearchChange: (query: string) => void;
  };
  filterState: {
    statusFilter: string;
    onStatusFilterChange: (status: string) => void;
    dateFilter: Date | null;
    onDateFilterChange: (date: Date | null) => void;
  };
  pageState: {
    currentPage: number;
    onPageChange: (details: { page: number }) => void;
    onRefresh: () => void;
  };
  folders?: FolderPublic[];
}

const PER_PAGE = 5;

// StatusBadge 컴포넌트 (외부 정의)
interface StatusBadgeProps {
  status?: string;
  error?: string;
}

const StatusBadge = ({ status, error }: StatusBadgeProps) => {
  const getStatusColor = (status?: string) => {
    switch (status) {
      case "PENDING":
        return "yellow";
      case "PROCESSING":
        return "blue";
      case "COMPLETED":
        return "green";
      case "FAILED":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case "PENDING":
        return "대기 중";
      case "PROCESSING":
        return "학습 중";
      case "COMPLETED":
        return "학습 완료";
      case "FAILED":
        return "학습 실패";
      default:
        return "알 수 없음";
    }
  };

  return (
    <Box>
      <Text
        as="span"
        px={2}
        py={1}
        borderRadius="full"
        fontSize="xs"
        fontWeight="medium"
        bg={`${getStatusColor(status)}.100`}
        color={`${getStatusColor(status)}.700`}
      >
        {getStatusText(status)}
      </Text>
      {error && status !== "FAILED" && (
        <Text fontSize="xs" color="red.500" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
};

export const FileList = ({
  selectedFolderId,
  fileState,
  searchState,
  filterState,
  pageState,
  folders,
}: FileListProps) => {
  const [isAddFileOpen, setIsAddFileOpen] = useState(false);

  const COLUMN_WIDTHS = {
    action: "40px",
    name: "280px", // 권한 컬럼 제거로 인한 너비 증가
    status: "140px",
    date: "160px",
    size: "120px",
  };

  // 상태 필터 옵션 수정 - 실제 DocumentStatus와 매칭
  const statusFilterOptions = createListCollection({
    items: [
      { label: "전체", value: "ALL" },
      { label: "대기 중", value: "PENDING" },
      { label: "학습 중", value: "PROCESSING" },
      { label: "학습 완료", value: "COMPLETED" },
      { label: "학습 실패", value: "FAILED" },
    ],
  });

  // 필터링된 파일 목록 - 검색어, 상태 필터, 날짜 필터 모두 적용
  const filteredFiles = useMemo(() => {
    const filtered = fileState.files.filter((file: FileItem) => {
      // 검색어 필터링
      const matchesSearch = file.name
        .toLowerCase()
        .includes(searchState.searchQuery.toLowerCase());

      // 상태 필터링 - "전체" 선택 시 모든 파일 표시
      const matchesStatus =
        filterState.statusFilter === "ALL" ||
        filterState.statusFilter === "" ||
        (file.status && file.status === filterState.statusFilter);

      // 날짜 필터링
      let matchesDate = true;
      if (filterState.dateFilter) {
        const fileDate = new Date(file.lastModified);
        const filterDate = new Date(filterState.dateFilter);

        // 같은 날짜인지 확인 (년, 월, 일만 비교)
        matchesDate =
          fileDate.getFullYear() === filterDate.getFullYear() &&
          fileDate.getMonth() === filterDate.getMonth() &&
          fileDate.getDate() === filterDate.getDate();
      }

      return matchesSearch && matchesStatus && matchesDate;
    });

    return filtered;
  }, [
    fileState.files,
    searchState.searchQuery,
    filterState.statusFilter,
    filterState.dateFilter,
  ]);

  const pagedFiles = useMemo(
    () =>
      filteredFiles.slice(
        (pageState.currentPage - 1) * PER_PAGE,
        pageState.currentPage * PER_PAGE
      ),
    [filteredFiles, pageState.currentPage]
  );

  const handleSelectAll = () => {
    // 현재 페이지의 모든 파일이 선택되어 있는지 확인
    const allSelected = pagedFiles.every(
      (file) =>
        file.document_id && fileState.selectedFiles.includes(file.document_id)
    );

    if (allSelected) {
      // 모든 파일이 선택되어 있으면 선택 해제
      const documentIdsToRemove = pagedFiles
        .filter((file) => file.document_id)
        .map((file) => file.document_id)
        .filter((id): id is string => id !== undefined)
        .join(",");
      if (documentIdsToRemove) {
        fileState.onFileSelect(documentIdsToRemove);
      }
    } else {
      // 일부만 선택되어 있거나 아무것도 선택되지 않은 경우 전체 선택
      const documentIdsToAdd = pagedFiles
        .filter(
          (file) =>
            file.document_id &&
            !fileState.selectedFiles.includes(file.document_id)
        )
        .map((file) => file.document_id)
        .filter((id): id is string => id !== undefined)
        .join(",");
      if (documentIdsToAdd) {
        fileState.onFileSelect(documentIdsToAdd);
      }
    }
  };

  const handleFileHover = (fileId: string | null) => {
    fileState.onFileHover(fileId || undefined);
  };

  // 현재 폴더 객체를 찾음

  return (
    <Box>
      <HStack justify="flex-end" pt={2} pb={2} gap={4}>
        <AddFile
          isOpen={isAddFileOpen}
          setIsOpen={setIsAddFileOpen}
          currentFolder={
            selectedFolderId
              ? folders?.find((f) => f.id === selectedFolderId)
              : undefined
          }
        />
      </HStack>

      <Flex w="100%" align="center" mb={2}>
        <Box
          w={COLUMN_WIDTHS.action}
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="40px"
          m={1}
        >
          <Icon
            as={FiTrash2}
            boxSize={7}
            color={
              selectedFolderId && fileState.selectedFiles.length > 0
                ? "red.500"
                : "gray.300"
            }
            cursor={
              selectedFolderId && fileState.selectedFiles.length > 0
                ? "pointer"
                : "not-allowed"
            }
            aria-label="삭제"
            onClick={fileState.onDeleteFiles}
          />
        </Box>
        <Box w={COLUMN_WIDTHS.name} px={2} position="relative">
          <Input
            placeholder="파일 검색"
            pl={10}
            value={searchState.searchQuery}
            onChange={(e) => searchState.onSearchChange(e.target.value)}
          />
          <Box
            position="absolute"
            left={5}
            top="50%"
            transform="translateY(-50%)"
          >
            <FiSearch size={20} color="gray.500" />
          </Box>
        </Box>
        <Box w={COLUMN_WIDTHS.status} px={2}>
          <select
            value={filterState.statusFilter}
            onChange={(e) => {
              filterState.onStatusFilterChange(e.target.value);
            }}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "14px",
              backgroundColor: "white",
              color: "#2d3748",
              cursor: "pointer",
              outline: "none",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#3182ce";
              e.target.style.boxShadow = "0 0 0 1px #3182ce";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          >
            {statusFilterOptions.items.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Box>
        <Box w={COLUMN_WIDTHS.date} px={2}>
          <ReactDatePicker
            selected={filterState.dateFilter}
            onChange={(date) => {
              filterState.onDateFilterChange(date);
            }}
            dateFormat="yyyy/MM/dd"
            placeholderText="날짜 선택"
            isClearable={true}
            customInput={
              <Input
                border="1px solid"
                borderColor="gray.300"
                w="100%"
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  color: "#2d3748",
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3182ce";
                  e.target.style.boxShadow = "0 0 0 1px #3182ce";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "none";
                }}
              />
            }
          />
        </Box>
        <Box flex="1" />
        <Button value="refresh" my={4} onClick={pageState.onRefresh}>
          새로고침
          <Icon as={FiRefreshCcw} fontSize="16px" ml={1} />
        </Button>
      </Flex>

      <Box
        w="100%"
        mt={2}
        border="1px solid #e2e8f0"
        borderRadius="md"
        overflow="hidden"
      >
        <Flex direction="column" w="100%">
          {/* 헤더 */}
          <Flex
            w="100%"
            align="center"
            bg="#cce3fd"
            borderBottom="1px solid #e2e8f0"
            fontWeight="bold"
            fontSize="md"
            height="40px"
          >
            <Box
              w={COLUMN_WIDTHS.action}
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="40px"
              p={0}
              m={1}
            >
              <Checkbox.Root
                checked={
                  pagedFiles.some(
                    (file) =>
                      file.document_id &&
                      fileState.selectedFiles.includes(file.document_id)
                  ) &&
                  !pagedFiles.every(
                    (file) =>
                      file.document_id &&
                      fileState.selectedFiles.includes(file.document_id)
                  )
                    ? "indeterminate"
                    : pagedFiles.length > 0 &&
                      pagedFiles.every(
                        (file) =>
                          file.document_id &&
                          fileState.selectedFiles.includes(file.document_id)
                      )
                }
                onCheckedChange={handleSelectAll}
                size="sm"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control
                  border="1px solid"
                  borderColor="blue.500"
                  _checked={{ bg: "blue.500" }}
                  _indeterminate={{ bg: "blue.500" }}
                >
                  <Checkbox.Indicator />
                </Checkbox.Control>
              </Checkbox.Root>
            </Box>
            <Box
              w={COLUMN_WIDTHS.name}
              textAlign="left"
              px={2}
              maxW={COLUMN_WIDTHS.name}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              title="파일 이름"
            >
              파일 이름
            </Box>
            <Box w={COLUMN_WIDTHS.status} textAlign="left" px={2}>
              상태
            </Box>
            <Box w={COLUMN_WIDTHS.date} textAlign="left" px={2}>
              마지막 수정
            </Box>
            <Box w={COLUMN_WIDTHS.size} textAlign="left" px={2}>
              파일 크기
            </Box>
            <Box w="80px" textAlign="right" px={2} />
          </Flex>

          {/* 파일 목록 */}
          <Box>
            {selectedFolderId ? (
              pagedFiles.length === 0 ? (
                <Box py={8} textAlign="center" color="gray.400">
                  파일이 없습니다.
                </Box>
              ) : (
                pagedFiles.map((file) => (
                  <Flex
                    key={file.document_id || file.name}
                    w="100%"
                    align="center"
                    borderBottom="1px solid #f1f1f1"
                    fontSize="sm"
                    height="40px"
                    bg={
                      file.document_id &&
                      fileState.selectedFiles.includes(file.document_id)
                        ? "#bbdefb"
                        : fileState.hoveredFileId === file.document_id
                          ? "#f5f5f5"
                          : "white"
                    }
                    transition="all 0.2s"
                    onMouseEnter={() =>
                      handleFileHover(file.document_id || null)
                    }
                    onMouseLeave={() => handleFileHover(null)}
                    style={{
                      cursor: "pointer",
                      borderLeft:
                        file.document_id &&
                        fileState.selectedFiles.includes(file.document_id)
                          ? "4px solid #1976d2"
                          : "4px solid transparent",
                    }}
                    _last={{ borderBottom: "none" }}
                  >
                    <Box
                      w={COLUMN_WIDTHS.action}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="40px"
                      p={0}
                      m={0}
                    >
                      <Checkbox.Root
                        checked={
                          file.document_id
                            ? fileState.selectedFiles.includes(file.document_id)
                            : false
                        }
                        onCheckedChange={() =>
                          file.document_id &&
                          fileState.onFileSelect(file.document_id)
                        }
                        size="sm"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control
                          border="1px solid"
                          borderColor="blue.500"
                          _checked={{ bg: "blue.500" }}
                          _indeterminate={{ bg: "blue.500" }}
                        >
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                      </Checkbox.Root>
                    </Box>
                    <Box
                      w={COLUMN_WIDTHS.name}
                      textAlign="left"
                      px={2}
                      maxW={COLUMN_WIDTHS.name}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      title={file.name}
                    >
                      {file.name}
                    </Box>
                    <Box w={COLUMN_WIDTHS.status} textAlign="left" px={2}>
                      <StatusBadge
                        status={file.status}
                        error={file.error_message}
                      />
                    </Box>
                    <Box w={COLUMN_WIDTHS.date} textAlign="left" px={2}>
                      {new Date(file.lastModified || "").toLocaleString(
                        "ko-KR",
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Box>
                    <Box w={COLUMN_WIDTHS.size} textAlign="left" px={2}>
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </Box>
                    <Box flex="1" />
                    <Box
                      minW="60px"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      gap={2}
                      pr={2}
                    >
                      <Icon
                        as={FiDownload}
                        boxSize={5}
                        color={
                          file.document_id &&
                          fileState.selectedFiles.includes(file.document_id)
                            ? "blue.500"
                            : "gray.500"
                        }
                        cursor="pointer"
                        aria-label="다운로드"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedFolderId) {
                            fileState.onDownload(file.object_name);
                          }
                        }}
                      />
                    </Box>
                  </Flex>
                ))
              )
            ) : (
              <Box py={8} textAlign="center">
                <Text color="gray.500">
                  폴더를 선택하거나 새 폴더를 만들어주세요.
                </Text>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>

      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={filteredFiles.length}
          pageSize={PER_PAGE}
          onPageChange={pageState.onPageChange}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </Box>
  );
};

export default FileList;
